import { useEffect, useState } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { calcStreaks, parseEnGbDate } from './dateUtils';
import { isSetLogged } from './workoutUtils';

export default function ProgressCharts({ workoutLog, language, weightUnit = 'kg' }) {
  const PERIODS = [
    { label: language === 'da' ? '1U' : '1W', days: 7 },
    { label: language === 'da' ? '2U' : '2W', days: 14 },
    { label: language === 'da' ? '1M' : '1M', days: 30 },
    { label: language === 'da' ? 'Alt' : 'All', days: 9999 },
  ];

  const [selectedExercise, setSelectedExercise] = useState('');
  const [period, setPeriod] = useState(9999);

  const { current, longest } = calcStreaks(workoutLog);

  const allExercises = [...new Set(
    Object.values(workoutLog).flatMap(w =>
      (w.exercises || [])
        .filter((e) => (e.sets || []).some(isSetLogged))
        .map((e) => e.name)
    )
  )].sort((a, b) => a.localeCompare(b, language === 'da' ? 'da' : 'en', { sensitivity: 'base' }));

  useEffect(() => {
    if (selectedExercise && allExercises.includes(selectedExercise)) return;
    if (allExercises.length === 0) return;
    const datesNewest = Object.keys(workoutLog).sort((a, b) => {
      const da = parseEnGbDate(a);
      const db = parseEnGbDate(b);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return db - da;
    });
    let recent = '';
    for (const date of datesNewest) {
      const match = (workoutLog[date].exercises || []).find((e) => (e.sets || []).some(isSetLogged));
      if (match?.name) { recent = match.name; break; }
    }
    setSelectedExercise(recent || allExercises[0]);
  }, [allExercises, selectedExercise, workoutLog]);

  const filteredDates = Object.keys(workoutLog)
    .filter(date => {
      if (period === 9999) return true;
      const dateObj = parseEnGbDate(date);
      if (!dateObj) return false;
      const cutoff = new Date();
      cutoff.setDate(cutoff.getDate() - period);
      return dateObj >= cutoff;
    })
    .sort((a, b) => {
      const da = parseEnGbDate(a);
      const db = parseEnGbDate(b);
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da - db;
    });

  const chartData = filteredDates
    .map((date) => {
      const exercise = workoutLog[date].exercises?.find((e) => e.name === selectedExercise);
      if (!exercise) return null;
      const logged = (exercise.sets || []).filter(isSetLogged);
      if (logged.length === 0) return null;
      const timed = exercise.trackingType === 'timed' || logged.every((s) => s.durationMinutes !== undefined && s.durationMinutes !== '');
      if (timed) {
        const maxDuration = Math.max(...logged.map((s) => parseFloat(s.durationMinutes) || 0));
        const totalDuration = logged.reduce((sum, s) => sum + (parseFloat(s.durationMinutes) || 0), 0);
        return { date: date.slice(0, 5), maxDuration, totalDuration, maxWeight: 0, totalReps: 0 };
      }
      const maxWeight = Math.max(...logged.map((s) => parseFloat(s.weight) || 0));
      const totalReps = logged.reduce((sum, s) => sum + (parseInt(s.reps) || 0), 0);
      return { date: date.slice(0, 5), maxWeight, totalReps, maxDuration: 0, totalDuration: 0 };
    })
    .filter(Boolean);

  const selectedIsTimed = chartData.length > 0 && chartData.some((d) => d.maxDuration > 0);

  const noData = language === 'da' ? 'Ikke nok data endnu — log mindst 2 træninger med denne øvelse!' : 'Not enough data yet — log at least 2 sessions with this exercise!';
  const selectExercise = language === 'da' ? 'Øvelser' : 'Exercises';
  const maxWeightLabel = language === 'da' ? `Maks vægt (${weightUnit})` : `Max Weight (${weightUnit})`;
  const totalRepsLabel = language === 'da' ? 'Samlede gentagelser' : 'Total Reps';
  const maxDurationLabel = language === 'da' ? 'Maks varighed (min)' : 'Max Duration (min)';
  const totalDurationLabel = language === 'da' ? 'Samlet varighed (min)' : 'Total Duration (min)';

  return (
    <div>
      <div style={{ display: 'flex', gap: '12px', marginBottom: '20px' }}>
        <div style={{ flex: 1, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 900, fontFamily: 'Barlow Condensed, sans-serif', color: current > 0 ? 'var(--accent)' : 'var(--text-muted)' }}>
            {current > 0 ? current : '—'}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600, marginTop: '4px' }}>
            {language === 'da' ? 'Nuværende streak' : 'Current Streak'}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
            {language === 'da' ? 'uger' : 'weeks'}
          </div>
        </div>
        <div style={{ flex: 1, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px', textAlign: 'center' }}>
          <div style={{ fontSize: '2rem', fontWeight: 900, fontFamily: 'Barlow Condensed, sans-serif', color: 'var(--accent)' }}>
            {longest || '—'}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600, marginTop: '4px' }}>
            {language === 'da' ? 'Længste streak' : 'Longest Streak'}
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>
            {language === 'da' ? 'uger' : 'weeks'}
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
        {PERIODS.map(p => (
          <button key={p.days} className="btn btn-dark"
            style={{ fontSize: '12px', padding: '6px 12px', background: period === p.days ? 'var(--accent)' : 'var(--surface2)', color: period === p.days ? 'white' : 'var(--text)', border: '1px solid var(--border)' }}
            onClick={() => setPeriod(p.days)}>
            {p.label}
          </button>
        ))}
      </div>

      <select
        className="input-field wide"
        value={selectedExercise}
        onChange={(e) => setSelectedExercise(e.target.value)}
        aria-label={selectExercise}
        style={{ width: '100%', marginBottom: '16px' }}
      >
        <option value="">{selectExercise}</option>
        {allExercises.map(ex => (
          <option key={ex} value={ex}>{ex}</option>
        ))}
      </select>


      {selectedExercise && chartData.length < 2 && (
        <p style={{ color: 'var(--text-muted)', fontSize: '14px' }}>{noData}</p>
      )}

      {selectedExercise && chartData.length >= 2 && (
        <>
          {selectedIsTimed ? (
            <>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>{maxDurationLabel}</p>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="date" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
                  <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '8px' }} />
                  <Line type="monotone" dataKey="maxDuration" stroke="var(--accent)" strokeWidth={2} dot={{ fill: 'var(--accent)' }} />
                </LineChart>
              </ResponsiveContainer>

              <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '8px', marginTop: '16px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>{totalDurationLabel}</p>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="date" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
                  <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '8px' }} />
                  <Line type="monotone" dataKey="totalDuration" stroke="#4CAF50" strokeWidth={2} dot={{ fill: '#4CAF50' }} />
                </LineChart>
              </ResponsiveContainer>
            </>
          ) : (
            <>
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>{maxWeightLabel}</p>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="date" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
                  <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '8px' }} />
                  <Line type="monotone" dataKey="maxWeight" stroke="var(--accent)" strokeWidth={2} dot={{ fill: 'var(--accent)' }} />
                </LineChart>
              </ResponsiveContainer>

              <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '8px', marginTop: '16px', textTransform: 'uppercase', letterSpacing: '1px', fontWeight: 600 }}>{totalRepsLabel}</p>
              <ResponsiveContainer width="100%" height={180}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="date" tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
                  <YAxis tick={{ fill: 'var(--text-muted)', fontSize: 11 }} />
                  <Tooltip contentStyle={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '8px' }} />
                  <Line type="monotone" dataKey="totalReps" stroke="#4CAF50" strokeWidth={2} dot={{ fill: '#4CAF50' }} />
                </LineChart>
              </ResponsiveContainer>
            </>
          )}
        </>
      )}
    </div>
  );
}